import { Box, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { PortfolioData } from "../../App.tsx";
import { usePortfolio } from "../../context/PortfolioContext";
import { PortfolioFilterProvider } from "../../context/PortfolioFilterContext";
import { useTheme } from "../../context/ThemeContext";
import { usePersona } from "../../context/usePersona";
import CenteredMessageBox from "../general/CenteredMessageBox";
import { SegmentedControl } from "../general/SegmentedControl";
import { PortfolioFilterBar } from "./PortfolioFilterBar";
import {
  PortfolioNavigation,
  portfolioPageAt,
  portfolioPagesOf,
} from "./PortfolioNavigation";

/** The pages of the portfolio are no wider than this, in px. */
const MAX_WIDTH = 1080;

/**
 * The room around the filter bar, in theme spacing units: while the page is
 * scrolled, what passes under the bar is covered this far above and below it.
 */
const FILTER_BAR_ROOM = 1;

/**
 * The frame of the pages of the portfolio: the navigation beside them, and
 * over each the title of the page, what it is about and the filters. With no
 * case to show, it says so in place of the page. The portfolio itself is no
 * page: it leads on to the first page of the role.
 */
export const PortfolioPage = () => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "portfolio" });

  const {
    caseCounts: { total },
    stats: { settlementAdjusted },
  } = useLoaderData<PortfolioData>();

  const { numberOfCases, clients } = usePortfolio();
  const numberOfClients = Object.keys(clients.stats).length;

  const location = useLocation();
  const navigate = useNavigate();
  const page = portfolioPageAt(location.pathname);
  // kept in the address, like the filters, so that the loader asks for it
  const params = new URLSearchParams(location.search);
  const shownResults =
    params.get("results") === "adjusted" ? "adjusted" : "litigation";
  const showResults = (results: "litigation" | "adjusted") => {
    if (results === "adjusted") params.set("results", results);
    else params.delete("results");
    navigate({ ...location, search: params.toString() }, { replace: true });
  };
  // the settlements page shows the settlements that happened, which the
  // settlement-adjusted results leave as they are
  const switchesResults = !!settlementAdjusted && page !== "settlements";
  const { active: persona } = usePersona();
  const filterBarRef = useRef<HTMLDivElement>(null);

  // scroll to anchor in location or top if no anchor hash is given
  useEffect(() => {
    if (!location.hash) {
      // scroll to top of page
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // there is an anchor hash, scroll to it
    const element = document.getElementById(location.hash.substring(1));
    const filterBar = filterBarRef.current;
    if (element && filterBar) {
      // the anchor lands under what lies over the page: the app bar, which
      // the filter bar sticks to, and the filter bar itself
      const covered =
        parseFloat(getComputedStyle(filterBar).top) + filterBar.offsetHeight;
      const top =
        element.getBoundingClientRect().top + window.scrollY - covered;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [location]);

  if (!page) {
    return (
      <Navigate
        to={{
          pathname: `/portfolio/${portfolioPagesOf(persona)[0]}`,
          search: location.search,
        }}
        replace
      />
    );
  }

  const emptyMessage =
    total === 0
      ? // the user has no case at all
        t("noCases")
      : numberOfCases === 0
        ? // there are cases, but the filters let none through
          t("noFilterMatches")
        : numberOfClients === 0
          ? // without a client the firm is in none of the cases, and there
            // is nothing to analyse
            t("noClients")
          : undefined;

  return (
    <PortfolioFilterProvider>
      <Box sx={{ display: "flex" }}>
        <PortfolioNavigation />
        <Box
          component="main"
          sx={{ flexGrow: 1, minWidth: 0, px: 3.5, pt: 3.25, pb: 7.5 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.25,
              maxWidth: MAX_WIDTH,
              mx: "auto",
            }}
          >
            {/* the title, and beside it which results the pages show */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  component="h1"
                  sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
                >
                  {t(`section.${page}`)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
                >
                  {t(`description.${page}`)}
                </Typography>
              </Box>
              {switchesResults && (
                <SegmentedControl
                  ariaLabel={t("results.label")}
                  value={shownResults}
                  onChange={showResults}
                  options={[
                    { value: "litigation", label: t("results.litigation") },
                    { value: "adjusted", label: t("results.adjusted") },
                  ]}
                />
              )}
            </Box>

            {/* the filters stay at hand under the app bar while the page is
                scrolled; the room around them hides what passes beneath */}
            <Box
              ref={filterBarRef}
              sx={{
                position: "sticky",
                top: theme.mixins.toolbar.minHeight,
                zIndex: theme.zIndex.appBar,
                py: FILTER_BAR_ROOM,
                my: -FILTER_BAR_ROOM,
                bgcolor: "background.default",
              }}
            >
              <PortfolioFilterBar />
            </Box>

            {emptyMessage ? (
              <CenteredMessageBox message={emptyMessage} />
            ) : (
              <Outlet />
            )}
          </Box>
        </Box>
      </Box>
    </PortfolioFilterProvider>
  );
};
